import { MovieCardProps, MovieWithDateFilter } from "@picovico/ui";
import {
  isShowBookable,
  isShowViewable,
  sortAdvanceBookingFirst,
} from "@picovico/ui/constant/enums";
import {
  convertMinutesToHours,
  convertTimeTo12HourFormat,
} from "@picovico/utils";

import { getPageRoutes } from "@pageRoutes";
import { useGetNowShowingQuery } from "@store/services/movie";
import { format } from "date-fns";
import { minBy } from "lodash";
import { useState } from "react";
import { useAppSelector } from "store/hooks";
import { ShowInfo } from "types/movie";

function NowShowing({
  showTimeList,
  toShowGenre,
  releaseDateFormat = "MMMM d yyyy",
  showDateSelector = true,
  prioritizeAdvanceBooking = false,
  ...props
}: Pick<
  MovieCardProps,
  "showTimeList" | "toShowGenre" | "releaseDateFormat" | "showDateSelector"
> & {
  /** List advance-booking movies before the rest. */
  prioritizeAdvanceBooking?: boolean;
}) {
  const [currentDate, setCurrentDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );
  const { advanceShowDays } = useAppSelector((state) => state.cineSetting);
  const { cinema_hall } = useAppSelector((state) => state.activeCinemaHall);
  const { isLoading, data, refetch, isFetching } = useGetNowShowingQuery(
    {
      ...(showDateSelector
        ? { show_date: currentDate }
        : { advance_days: true }),
      show_date: currentDate,
      cinema_hall: cinema_hall,
    },
    {
      skip: !cinema_hall,
    },
  );

  const getStartDate = (show: ShowInfo[]) => {
    const earliest = minBy(show, (s) => new Date(s.show_start_date_time || 0));
    return earliest?.show_start_date_time
      ? format(new Date(earliest.show_start_date_time), "yyyy-MM-dd")
      : "";
  };

  const nowShowingMovies =
    prioritizeAdvanceBooking && data?.data
      ? sortAdvanceBookingFirst(data.data, (m) => new Date(m.release_date))
      : data?.data;

  const movieCardProps = {
    movies:
      nowShowingMovies?.map(
        ({
          idx,
          grade,
          duration,
          poster,
          showinfo,
          release_date,
          genre,
          tag,
          roll_quality,
          ...item
        }) => ({
          title: item.name,
          poster,
          grade,
          genre,
          tag,
          roll_quality,
          duration: convertMinutesToHours(duration),
          toShowGenre,
          idx,
          releaseDate: format(new Date(release_date), releaseDateFormat),
          videoLink: item.trailer,
          detailLink: getPageRoutes(
            "movie_detail",
            {
              movieIdx: idx,
              cinemaHallIdx: cinema_hall as string,
            },
            {
              ...(showDateSelector && { date: currentDate }),
              startDate: getStartDate(showinfo),
            },
          ),
          movieShowTimes:
            showinfo
              ?.slice()
              ?.sort((a, b) =>
                a.show_start_date_time.localeCompare(b.show_start_date_time),
              )
              ?.map((i) => ({
                ...i,
                start_time: convertTimeTo12HourFormat(
                  i.show_start_date_time.slice(11, 16),
                ),
                available: isShowBookable(i.show_status),
                viewable: isShowViewable(
                  i.show_status,
                  i.show_ended,
                  i.seat_view_allowed,
                ),
                showLink: getPageRoutes("audi_detail", {
                  movieIdx: idx,
                  showIdx: i.show_idx,
                }),
              })) || [],
          isNowShowing: true,
          showTimeList,
        }),
      ) || [],
    dateSelectorMaxDays: advanceShowDays,
    showDateSelector,
    title: "Now Showing",
    isLoading: isLoading || isFetching,

    onDateChange: (date: Date) => {
      setCurrentDate(format(date, "yyyy-MM-dd"));
      refetch();
    },

    ...props,
  };

  return cinema_hall && <MovieWithDateFilter {...movieCardProps} />;
}

NowShowing.defaultProps = {
  showTimeList: true,
};

export default NowShowing;
